'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { CameraLens } from '@/types/fleet';
import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * Direct-manipulation camera gestures for the COP video window (issue #244).
 *
 * The video area IS the control: a single-pointer drag slews the gimbal in the
 * direction of the drag, a two-pointer pinch steps the zoom, a double-tap steps
 * in. No modifier key is ever required — a gloved finger on a tablet is a
 * first-class operator here. Every verb now also has a single-pointer button on
 * the control bar: zoom and recenter always did, and the gimbal nudge keys
 * (`GIMBAL_NUDGE_DEG` in [CameraControlOverlay]) close what F10 left open — a
 * pointing operator who cannot drag can now aim, not just recenter.
 *
 * The keys send the same RELATIVE deltas this hook does, and agree with it on
 * sign, so the two paths cannot disagree about which way is up. What a button
 * still cannot do is aim in ONE gesture to an arbitrary angle; drag remains the
 * fast path, and any surface that scrolls has to be button-only by design (the
 * details panel was, until video moved out of it to a floating window).
 *
 * The px→deg and throttle/quantization arithmetic lives in the PURE functions
 * below so it unit-tests without a DOM; the hook is only the pointer-event
 * bookkeeping around them.
 *
 * The client never computes a target zoom ratio or an absolute gimbal angle:
 * it sends RELATIVE degree deltas and quantized zoom STEPS, and the aircraft
 * owns the clamping and the step→ratio table. A stale `zoom_ratio` here can
 * therefore mis-scale a drag slightly, but can never point the camera somewhere
 * the operator did not drag it.
 */

/**
 * Horizontal field of view assumed at 1× zoom, used only to scale pixels of
 * drag into degrees of slew so a drag feels like grabbing the picture.
 * // VERIFY on bench — 60° is a plausible DJI wide-camera HFOV, not a measured
 * one; a wrong value changes drag FEEL (over/under-shoot), never safety, since
 * only relative deltas are ever sent.
 */
export const ASSUMED_HFOV_DEG_AT_1X = 60;

/** Pointer travel below this is hand tremor, not a slew. */
export const DRAG_DEAD_ZONE_PX = 6;

/** At most one gimbal (or zoom-step) command per this window during a gesture.
 *  For SLEW the remainder goes out on pointerup, so the camera ends where the
 *  drag ended. For PINCH the release flush is at most ONE step — see the
 *  pointerup handler for why more would be a lie, not a catch-up. */
export const CAMERA_SEND_INTERVAL_MS = 250;

/** Each ≥1.25× of cumulative pinch scale = one zoom step. */
export const PINCH_STEP_FACTOR = 1.25;

/** Two taps this close in time and space are a double-tap. */
export const DOUBLE_TAP_MS = 300;
export const DOUBLE_TAP_RADIUS_PX = 24;

/** When the element cannot be measured (jsdom, display:none), assume a typical
 *  window width rather than dividing by zero. */
export const FALLBACK_VIEWPORT_WIDTH_PX = 640;

// ---------------------------------------------------------------------------
// Pure functions
// ---------------------------------------------------------------------------

/** Degrees of gimbal travel per pixel of drag: the assumed 1× HFOV narrows
 *  linearly with zoom, so a drag at 4× moves a quarter of what it moves at 1×. */
export function degPerPx(viewportWidthPx: number, zoomRatio: number | undefined): number {
  const zoom = typeof zoomRatio === 'number' && zoomRatio > 0 ? zoomRatio : 1;
  const width = viewportWidthPx > 0 ? viewportWidthPx : FALLBACK_VIEWPORT_WIDTH_PX;
  return ASSUMED_HFOV_DEG_AT_1X / zoom / width;
}

export function exceedsDeadZone(dxPx: number, dyPx: number, deadZonePx = DRAG_DEAD_ZONE_PX): boolean {
  return Math.hypot(dxPx, dyPx) >= deadZonePx;
}

/**
 * Total slew (degrees) a drag of (dxPx, dyPx) asks for. Screen Y grows
 * downward, so drag UP = pitch UP means pitch takes the negated dy. A disabled
 * axis contributes 0 — that is the axis-locked drag when the payload has no
 * yaw axis.
 */
export function slewDeltasDeg({
  dxPx,
  dyPx,
  degPerPxValue,
  pitchEnabled,
  yawEnabled,
}: {
  dxPx: number;
  dyPx: number;
  degPerPxValue: number;
  pitchEnabled: boolean;
  yawEnabled: boolean;
}): { pitchDeg: number; yawDeg: number } {
  return {
    pitchDeg: pitchEnabled ? -dyPx * degPerPxValue : 0,
    yawDeg: yawEnabled ? dxPx * degPerPxValue : 0,
  };
}

export interface SlewThrottleState {
  lastSentAtMs: number;
  sentPitchDeg: number;
  sentYawDeg: number;
}

export const idleSlewState = (): SlewThrottleState => ({
  lastSentAtMs: Number.NEGATIVE_INFINITY,
  sentPitchDeg: 0,
  sentYawDeg: 0,
});

/** One decimal of a degree is below anything a gimbal resolves; also avoids
 *  drip-feeding float noise as commands. */
function roundDeg(v: number): number {
  const r = Math.round(v * 10) / 10;
  return r === 0 ? 0 : r;
}

/**
 * The throttle: given the TOTAL slew the drag has asked for so far, emit the
 * not-yet-sent remainder — but at most once per interval, unless `flush`
 * (pointerup) forces the tail out. Returns the unchanged state and a null
 * command when there is nothing to send or the window has not elapsed.
 */
export function drainSlew(
  state: SlewThrottleState,
  totalPitchDeg: number,
  totalYawDeg: number,
  nowMs: number,
  { flush = false, intervalMs = CAMERA_SEND_INTERVAL_MS }: { flush?: boolean; intervalMs?: number } = {},
): { state: SlewThrottleState; command: { pitch_deg?: number; yaw_deg?: number } | null } {
  if (!flush && nowMs - state.lastSentAtMs < intervalMs) {
    return { state, command: null };
  }
  const pitch = roundDeg(totalPitchDeg - state.sentPitchDeg);
  const yaw = roundDeg(totalYawDeg - state.sentYawDeg);
  if (pitch === 0 && yaw === 0) {
    return { state, command: null };
  }
  const command: { pitch_deg?: number; yaw_deg?: number } = {};
  if (pitch !== 0) command.pitch_deg = pitch;
  if (yaw !== 0) command.yaw_deg = yaw;
  return {
    state: {
      lastSentAtMs: nowMs,
      sentPitchDeg: state.sentPitchDeg + pitch,
      sentYawDeg: state.sentYawDeg + yaw,
    },
    command,
  };
}

/**
 * How many whole zoom steps a cumulative pinch scale amounts to. Signed:
 * spreading past 1.25× is +1, pinching in past 1/1.25 is −1. Quantized toward
 * zero so a hesitant pinch emits nothing until it has really crossed a step.
 */
export function pinchStepTarget(scale: number, stepFactor = PINCH_STEP_FACTOR): number {
  if (!(scale > 0)) return 0;
  const raw = Math.log(scale) / Math.log(stepFactor);
  // Float guard: scale === stepFactor^n computes as n ± ~1e-16, and truncation
  // must not eat the step the operator just crossed. Truncating a small
  // negative also yields -0; normalize it so "no steps" is one value.
  const steps = Math.trunc(raw + Math.sign(raw) * 1e-9);
  return steps === 0 ? 0 : steps;
}

export interface TapPoint {
  t: number;
  x: number;
  y: number;
}

export function isDoubleTap(
  prev: TapPoint | null,
  next: TapPoint,
  { maxDelayMs = DOUBLE_TAP_MS, maxDistPx = DOUBLE_TAP_RADIUS_PX }: { maxDelayMs?: number; maxDistPx?: number } = {},
): boolean {
  if (!prev) return false;
  return next.t - prev.t <= maxDelayMs && Math.hypot(next.x - prev.x, next.y - prev.y) <= maxDistPx;
}

// ---------------------------------------------------------------------------
// Readout formatting (military mono readout: GBL P −35.0° Y +004.5° · ZM 4.0×)
// ---------------------------------------------------------------------------

const MINUS = '−';
const NOT_REPORTED = '—'; // "—": absent means the aircraft did not report it, never 0.

function formatSignedDeg(v: number, intPad: number): string {
  const sign = v < 0 ? MINUS : '+';
  const [int, frac] = Math.abs(v).toFixed(1).split('.');
  return `${sign}${int.padStart(intPad, '0')}.${frac}°`;
}

export function formatPitchReadout(v: number | undefined): string {
  return typeof v === 'number' ? formatSignedDeg(v, 0) : NOT_REPORTED;
}

export function formatYawReadout(v: number | undefined): string {
  return typeof v === 'number' ? formatSignedDeg(v, 3) : NOT_REPORTED;
}

export function formatZoomReadout(v: number | undefined): string {
  return typeof v === 'number' ? `${v.toFixed(1)}×` : NOT_REPORTED;
}

/**
 * The operator-facing labels for the three lens words, and the order the keys
 * are laid out in.
 *
 * The same words and the same order as the RC's own LENS row (`LENS_LABELS` in
 * CameraScreen.kt) — an operator who has used one surface must not have to
 * relearn the other, and "IR" against "THERMAL" on two screens for one camera is
 * how that happens. Wire word in, label out: the protocol is never the button.
 */
export const LENS_LABELS: Readonly<Record<CameraLens, string>> = {
  wide: 'WIDE',
  zoom: 'ZOOM',
  ir: 'IR',
};

export const LENS_ORDER: readonly CameraLens[] = ['wide', 'zoom', 'ir'];

export function formatLensReadout(v: CameraLens | null | undefined): string {
  return v != null ? LENS_LABELS[v] : NOT_REPORTED;
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface UseCameraGesturesOptions {
  /** Per-axis verdicts from cameraCommandAvailability. An unavailable axis is
   *  never sent; the caller attaches NO handlers when nothing is available. */
  pitchAvailable: boolean;
  yawAvailable: boolean;
  zoomAvailable: boolean;
  /** Live zoom ratio, for px→deg scaling. Undefined = assume 1×. */
  zoomRatio: number | undefined;
  onGimbal: (params: { pitch_deg?: number; yaw_deg?: number }) => void;
  onZoomStep: (step: 'in' | 'out') => void;
}

export interface CameraGestureHandlers {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  onLostPointerCapture: (e: ReactPointerEvent<HTMLElement>) => void;
}

interface TrackedPointer {
  x: number;
  y: number;
  startX: number;
  startY: number;
}

interface PinchState {
  ids: [number, number];
  initialSpanPx: number;
  emittedSteps: number;
  lastSentAtMs: number;
}

export function useCameraGestures(options: UseCameraGesturesOptions): CameraGestureHandlers {
  // Read through a ref so in-flight gestures see the CURRENT zoom and callback,
  // and so the returned handlers stay referentially stable.
  const opts = useRef(options);
  useEffect(() => {
    opts.current = options;
  });

  const pointers = useRef<Map<number, TrackedPointer>>(new Map());
  const mode = useRef<'idle' | 'slew' | 'pinch'>('idle');
  /** True from the moment a second pointer lands until all are up — a finger
   *  that took part in a pinch must not read as a tap on its way out. */
  const multiTouch = useRef(false);
  const slew = useRef<SlewThrottleState>(idleSlewState());
  const slewDegPerPx = useRef(0);
  /**
   * Axis-enables LATCHED at slew start, like slewDegPerPx and for the same
   * reason: `drainSlew` diffs cumulative totals, so a live capability read that
   * flips mid-drag rewrites history — true→false makes the total drop to 0 and
   * the next drain emits an inverse "un-slew" (0 − sentYawDeg), and false→true
   * slams the whole accumulated backlog out as one command. A capability change
   * mid-drag ENDS the gesture instead (review finding F5).
   */
  const slewPitchEnabled = useRef(false);
  const slewYawEnabled = useRef(false);
  const pinch = useRef<PinchState | null>(null);
  const lastTap = useRef<TapPoint | null>(null);

  const resetGesture = useCallback(() => {
    mode.current = 'idle';
    slew.current = idleSlewState();
    slewDegPerPx.current = 0;
    slewPitchEnabled.current = false;
    slewYawEnabled.current = false;
    pinch.current = null;
  }, []);

  const pinchSpan = useCallback((): number | null => {
    const p = pinch.current;
    if (!p) return null;
    const a = pointers.current.get(p.ids[0]);
    const b = pointers.current.get(p.ids[1]);
    if (!a || !b) return null;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }, []);

  /** Emit at most `maxSteps` of the not-yet-emitted quantized steps. */
  const emitPinchSteps = useCallback((nowMs: number, maxSteps: number, ignoreThrottle: boolean) => {
    const p = pinch.current;
    if (!p) return;
    const span = pinchSpan();
    if (span === null || p.initialSpanPx <= 0) return;
    const target = pinchStepTarget(span / p.initialSpanPx);
    let emitted = 0;
    while (p.emittedSteps !== target && emitted < maxSteps) {
      if (!ignoreThrottle && nowMs - p.lastSentAtMs < CAMERA_SEND_INTERVAL_MS) return;
      const direction = target > p.emittedSteps ? 1 : -1;
      opts.current.onZoomStep(direction > 0 ? 'in' : 'out');
      p.emittedSteps += direction;
      p.lastSentAtMs = nowMs;
      emitted += 1;
    }
  }, [pinchSpan]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    // Right/middle mouse buttons belong to the browser, not the gimbal.
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      // A test environment or a pointer already gone — moves still bubble here.
    }
    pointers.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
    });

    if (pointers.current.size === 2) {
      multiTouch.current = true;
      // The second finger converts the gesture: any un-sent slew remainder is
      // dropped rather than flushed — the first finger's last pixels were the
      // pinch setup, not slew intent.
      slew.current = idleSlewState();
      if (opts.current.zoomAvailable) {
        const ids = Array.from(pointers.current.keys()) as [number, number];
        const a = pointers.current.get(ids[0]);
        const b = pointers.current.get(ids[1]);
        mode.current = 'pinch';
        pinch.current = {
          ids,
          initialSpanPx: a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0,
          emittedSteps: 0,
          lastSentAtMs: Number.NEGATIVE_INFINITY,
        };
      } else {
        mode.current = 'idle';
        pinch.current = null;
      }
    }
  }, []);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const p = pointers.current.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX;
    p.y = e.clientY;

    const now = Date.now();

    if (mode.current === 'pinch') {
      // Zoom capability withdrawn mid-pinch (availability flip): end the
      // gesture rather than keep stepping a control that is no longer offered.
      // The fingers stay tracked so their way out cannot read as taps.
      if (!opts.current.zoomAvailable) {
        resetGesture();
        return;
      }
      if (pinch.current?.ids.includes(e.pointerId)) {
        emitPinchSteps(now, 1, false);
      }
      return;
    }

    if (pointers.current.size !== 1 || multiTouch.current) return;
    const { pitchAvailable, yawAvailable, zoomRatio } = opts.current;
    const dx = p.x - p.startX;
    const dy = p.y - p.startY;

    if (mode.current === 'idle') {
      if (!pitchAvailable && !yawAvailable) return;
      if (!exceedsDeadZone(dx, dy)) return;
      mode.current = 'slew';
      slew.current = idleSlewState();
      // Measured once at slew start: the drag's feel should not change mid-
      // gesture as a zoom ack lands.
      const width = e.currentTarget.getBoundingClientRect?.()?.width ?? 0;
      slewDegPerPx.current = degPerPx(width, zoomRatio);
      // Latched with it (F5) — see the refs' comment for the failure modes a
      // live read causes when drainSlew diffs cumulative totals.
      slewPitchEnabled.current = pitchAvailable;
      slewYawEnabled.current = yawAvailable;
    }

    // An axis-enable that changed since slew start ends the gesture: no flush
    // (the un-sent remainder was asked of a capability set that no longer
    // exists) and the pointer is dropped so later moves cannot restart it.
    if (
      slewPitchEnabled.current !== opts.current.pitchAvailable ||
      slewYawEnabled.current !== opts.current.yawAvailable
    ) {
      // This was the single tracked pointer (guarded above), so the map is now
      // empty and multi-touch cannot be in progress.
      pointers.current.delete(e.pointerId);
      resetGesture();
      multiTouch.current = false;
      return;
    }

    const totals = slewDeltasDeg({
      dxPx: dx,
      dyPx: dy,
      degPerPxValue: slewDegPerPx.current,
      pitchEnabled: slewPitchEnabled.current,
      yawEnabled: slewYawEnabled.current,
    });
    const drained = drainSlew(slew.current, totals.pitchDeg, totals.yawDeg, now);
    slew.current = drained.state;
    if (drained.command) opts.current.onGimbal(drained.command);
  }, [emitPinchSteps, resetGesture]);

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const p = pointers.current.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX;
    p.y = e.clientY;
    const now = Date.now();

    if (mode.current === 'slew') {
      // The tail of the drag: whatever the throttle held back goes out now, so
      // the camera ends up exactly where the operator dragged it. Latched axes
      // (F5), same as every drain during the drag.
      const totals = slewDeltasDeg({
        dxPx: p.x - p.startX,
        dyPx: p.y - p.startY,
        degPerPxValue: slewDegPerPx.current,
        pitchEnabled: slewPitchEnabled.current,
        yawEnabled: slewYawEnabled.current,
      });
      const drained = drainSlew(slew.current, totals.pitchDeg, totals.yawDeg, now, { flush: true });
      if (drained.command) opts.current.onGimbal(drained.command);
      resetGesture();
    } else if (mode.current === 'pinch' && pinch.current?.ids.includes(e.pointerId)) {
      // Release flush: at most ONE step, not the whole throttled backlog.
      // Back-to-back `step` commands are each resolved by the aircraft against
      // the same stale zoom_ratio reading, so N flushed steps net ONE step of
      // actual zoom (confirmed in review, finding F7) — sending four is not
      // "nothing lost", it is three commands that lie about what they do.
      // Device-side compounding of queued steps is the follow-up that would
      // make a multi-step flush honest.
      emitPinchSteps(now, 1, true);
      resetGesture();
    } else if (
      mode.current === 'idle' &&
      !multiTouch.current &&
      !exceedsDeadZone(p.x - p.startX, p.y - p.startY)
    ) {
      // A tap. Two of them close together = one zoom step in; Alt on the second
      // click = out (desktop convenience — the − button is the universal path).
      const tap: TapPoint = { t: now, x: p.x, y: p.y };
      if (opts.current.zoomAvailable && isDoubleTap(lastTap.current, tap)) {
        opts.current.onZoomStep(e.altKey ? 'out' : 'in');
        lastTap.current = null;
      } else {
        lastTap.current = tap;
      }
    }

    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) {
      multiTouch.current = false;
      mode.current = 'idle';
    }
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      // Already released.
    }
  }, [emitPinchSteps, resetGesture]);

  const onPointerCancel = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    // The browser reclaimed the gesture (scroll, alt-tab, palm rejection).
    // Nothing is flushed: a cancelled gesture is one the operator did not
    // finish, and guessing at its tail could slew a camera nobody is dragging.
    pointers.current.delete(e.pointerId);
    resetGesture();
    if (pointers.current.size === 0) multiTouch.current = false;
  }, [resetGesture]);

  /**
   * Capture ended without a pointerup/cancel reaching us (alt-tab, the capture
   * stolen by another element, the surface torn down mid-drag). Before this
   * handler existed the pointer lingered in `pointers.current` forever and the
   * NEXT touch counted as a second finger — a phantom pinch on a single-finger
   * drag (review finding F6). Same no-flush semantics as cancel. On a normal
   * pointerup the browser fires this AFTER our up handler has already deleted
   * the entry, so the `has` guard makes that path a no-op.
   */
  const onLostPointerCapture = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.delete(e.pointerId);
    resetGesture();
    if (pointers.current.size === 0) multiTouch.current = false;
  }, [resetGesture]);

  // Unmount mid-gesture: drop every tracked pointer so a hook instance that
  // outlives its surface (the surface is conditionally rendered) cannot misread
  // the first touch after a remount as multi-touch (F6).
  useEffect(() => {
    const tracked = pointers.current;
    return () => {
      tracked.clear();
      multiTouch.current = false;
      resetGesture();
    };
  }, [resetGesture]);

  // The surface unmounts (all controls withdrawn) while the HOOK lives on: no
  // pointerup, cancel or lostpointercapture can reach a detached element, so
  // this is the only place the mid-drag pointers can be dropped before the
  // surface remounts and misreads the next touch as a second finger (F6).
  const anyAvailable = options.pitchAvailable || options.yawAvailable || options.zoomAvailable;
  useEffect(() => {
    if (anyAvailable) return;
    pointers.current.clear();
    multiTouch.current = false;
    resetGesture();
  }, [anyAvailable, resetGesture]);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onLostPointerCapture };
}
